/**
 * Private portfolio API wrapper. All calls send cookies via
 * `credentials: 'include'` so the `act_nda` httpOnly JWT cookie is included.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/private`;

async function parseErr(res) {
    try {
        const body = await res.json();
        if (typeof body.detail === "string") return body.detail;
        if (Array.isArray(body.detail))
            return body.detail
                .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
                .join(" ");
        return JSON.stringify(body);
    } catch {
        return res.statusText || "Request failed";
    }
}

async function doFetch(path, options = {}) {
    const res = await fetch(`${API}${path}`, {
        credentials: "include",
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = new Error(await parseErr(res));
        err.status = res.status;
        throw err;
    }
    if (res.status === 204) return null;
    return res.json();
}

export const privateApi = {
    async access(payload) {
        return doFetch("/access", {
            method: "POST",
            body: JSON.stringify(payload),
        });
    },
    async logout() {
        return doFetch("/logout", { method: "POST" });
    },
    async me() {
        return doFetch("/me", { method: "GET" });
    },
    /**
     * Retry /me once after 300ms on 401. Defensive against cookie-attach
     * race in some browsers (iframe contexts in particular).
     */
    async meWithRetry() {
        try {
            return await this.me();
        } catch (err) {
            if (err.status !== 401) throw err;
            await new Promise((r) => setTimeout(r, 300));
            return await this.me();
        }
    },
    async projects() {
        return doFetch("/projects", { method: "GET" });
    },
    async project(slug) {
        return doFetch(`/projects/${encodeURIComponent(slug)}`, { method: "GET" });
    },
};
